import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, ShieldCheck, Award, Zap, Target } from "lucide-react";
import PlaceholderImage from "@/components/PlaceholderImage";
import SectionReveal from "@/components/SectionReveal";
import ProductCard from "@/components/ProductCard";
import { productList, products } from "@/data/products";
import {
  HERO_BANNERS,
  PAGE_BANNERS,
  FLAVOUR_BANNERS,
  BENEFIT_BANNERS,
} from "@/data/assets";

/* ============ HERO SLIDES (image-only + CTA button) ============ */
const HERO_SLIDES = [
  {
    id: "brand",
    label: "Homepage Hero Banner",
    src: HERO_BANNERS.slide1,
    ctaLabel: "Explore Products",
    ctaTo: "/products",
  },
  {
    id: "whey",
    label: "Website Banner · Combat 100% Whey",
    src: HERO_BANNERS.slide2,
    ctaLabel: "View Combat Whey",
    ctaTo: "/products/combat-whey",
  },
  {
    id: "preworkout",
    label: "Website Banner · Combat Preworkout",
    src: HERO_BANNERS.slide3,
    ctaLabel: "View Combat Preworkout",
    ctaTo: "/products/combat-preworkout",
  },
];

const HIGHLIGHTS = [
  { icon: Zap,          title: "Combat-Ready",   body: "A focused range built to support serious, consistent training." },
  { icon: ShieldCheck,  title: "100% Genuine",   body: "Every genuine unit carries a TrueMPN authenticity sticker verifiable on-app." },
  { icon: Award,        title: "Global Legacy",  body: "A trusted international sports nutrition brand, now officially in India." },
  { icon: Target,       title: "Simple Nutrition", body: "Clean, straightforward formulas — no unnecessary noise, no filler." },
];

/* ============ THE COMBAT CREED — brand voice, no fabricated quotes ============ */
const CREED = [
  {
    kicker: "Quality",
    title: "Made For Athletes.",
    body: "Every product in the Combat range is designed for people who train seriously. Premium ingredients. Straightforward formulas. Nothing wasted.",
  },
  {
    kicker: "Legacy",
    title: "Trusted Globally.",
    body: "For over a decade, MusclePharm has fuelled athletes across strength, endurance and combat sports. That same standard now arrives in India.",
  },
  {
    kicker: "Authenticity",
    title: "Verifiable, Always.",
    body: "Every genuine unit sold in India carries a TrueMPN authenticity sticker. Scan the code. Confirm your unit in seconds. No guesswork.",
  },
];

/* ============ HERO CAROUSEL ============ */
function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const total = HERO_SLIDES.length;

  const goTo = useCallback((i) => setIndex(((i % total) + total) % total), [total]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);

  const slide = HERO_SLIDES[index];

  return (
    <section data-testid="hero-carousel" className="relative bg-white">
      <div className="relative">
        <div className="relative aspect-[16/9] lg:aspect-[21/9] overflow-hidden">
          <img
            key={slide.id}
            src={slide.src}
            alt={slide.label}
            data-testid={`hero-slide-${slide.id}`}
            data-placeholder-label={slide.label}
            className="absolute inset-0 w-full h-full object-cover animate-[mpSlideFade_0.6s_ease]"
          />
          {/* Subtle bottom gradient for CTA legibility */}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/50 to-transparent" />

          <div className="absolute inset-x-0 bottom-8 lg:bottom-12 z-10">
            <div className="mp-container flex justify-center lg:justify-start">
              <Link
                to={slide.ctaTo}
                data-testid={`hero-cta-${slide.id}`}
                className="mp-btn mp-btn-primary animate-[mpSlideFade_0.6s_ease]"
              >
                {slide.ctaLabel} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={prev}
          data-testid="hero-prev"
          aria-label="Previous slide"
          className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/40 hover:bg-white text-white hover:text-black border border-white/25 transition-colors z-20"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          onClick={next}
          data-testid="hero-next"
          aria-label="Next slide"
          className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/40 hover:bg-white text-white hover:text-black border border-white/25 transition-colors z-20"
        >
          <ChevronRight size={20} />
        </button>

        <div
          className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-20"
          data-testid="hero-dots"
        >
          {HERO_SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goTo(i)}
              data-testid={`hero-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 transition-all ${
                i === index ? "w-10 bg-white" : "w-4 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="border-y border-black/10 bg-white">
        <div className="mp-marquee-track flex whitespace-nowrap py-3 font-heading uppercase tracking-[0.3em] text-sm text-black">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center">
              {[
                "Combat 100% Whey",
                "Combat Preworkout",
                "Authenticity Verified",
                "Made For Athletes",
                "Officially In India",
                "Combat 100% Whey",
                "Combat Preworkout",
                "Authenticity Verified",
              ].map((t, i) => (
                <span key={`${k}-${i}`} className="mx-8 flex items-center gap-8">
                  {t}
                  <span
                    className="inline-block w-1.5 h-1.5"
                    style={{ background: "var(--mp-green)" }}
                  />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const combatWhey = products["combat-whey"];
  const combatPreworkout = products["combat-preworkout"];

  return (
    <>
      <HeroCarousel />

      {/* ============ FEATURED PRODUCTS ============ */}
      <section data-testid="featured-products-section" className="bg-white py-20 lg:py-24">
        <div className="mp-container">
          <SectionReveal>
            <div className="text-center mb-12">
              <div className="mp-eyebrow-green">The Combat Range</div>
              <h2 className="mt-3 font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.95]">
                Featured Products
              </h2>
              <p className="mt-4 max-w-xl mx-auto font-body text-black/65 leading-relaxed">
                Two focused formulas — engineered for Indian athletes who care about quality, consistency and results.
              </p>
            </div>
          </SectionReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {productList.map((p, i) => (
              <SectionReveal key={p.slug} delay={i * 120}>
                <ProductCard product={p} />
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BRAND STORY ============ */}
      <section data-testid="brand-story-section" className="bg-[color:var(--mp-off-white)] py-20 lg:py-28">
        <div className="mp-container grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <SectionReveal className="lg:col-span-6">
            <PlaceholderImage
              label="Brand Story Image"
              ratio="square"
              testId="brand-story-image"
              src={PAGE_BANNERS.brandStory}
              alt="MusclePharm — Built in the USA. Now in India."
              objectFit="contain"
            />
          </SectionReveal>
          <SectionReveal delay={120} className="lg:col-span-6">
            <div className="mp-eyebrow-green">Our Story</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
              Built in the USA.<br />
              Now in India.
            </h2>
            <div className="mt-6 space-y-4 font-body text-black/75 text-lg leading-relaxed max-w-xl">
              <p>
                MusclePharm has spent over a decade fuelling serious athletes — from combat sports arenas to everyday gym floors. Uncompromising formulas. Premium ingredients. Nothing wasted.
              </p>
              <p>
                That same commitment now arrives in India through an exclusive partnership with <strong>MusclePro Nutrition Private Limited</strong>, launching with a focused two-product Combat range and backed by TrueMPN authenticity verification on every unit.
              </p>
            </div>
            <div className="mt-8">
              <Link to="/about" data-testid="brand-story-cta" className="mp-btn mp-btn-primary">
                Read The Full Story
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ============ SERIES BANNERS — Combat Whey ============ */}
      <section data-testid="series-banners-section" className="bg-white py-20 lg:py-24">
        <div className="mp-container">
          <SectionReveal>
            <div className="mb-10">
              <div className="mp-eyebrow-green">Discover</div>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
                The Combat Series
              </h2>
            </div>
          </SectionReveal>

          {/* Whey banner */}
          <SectionReveal>
            <PlaceholderImage
              label="Product Benefit Banner · Combat 100% Whey"
              ratio="banner"
              testId="series-banner-whey"
              src={BENEFIT_BANNERS["combat-whey"]}
              alt="Combat 100% Whey — 24g Protein"
            />
          </SectionReveal>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            {combatWhey.flavours.map((f, i) => (
              <SectionReveal key={f.id} delay={i * 80}>
                <Link
                  to={`/products/combat-whey`}
                  data-testid={`series-whey-flavour-${f.id}`}
                  className="block bg-white border border-black/10 hover:border-black transition-colors group"
                >
                  <div className="overflow-hidden">
                    <PlaceholderImage
                      label={`Flavour Banner · ${f.name}`}
                      ratio="square"
                      src={FLAVOUR_BANNERS[f.id]}
                      alt={`${f.name} — Combat 100% Whey`}
                      className="transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="p-5 text-center">
                    <div className="font-display text-xl uppercase text-black">{f.name}</div>
                    <div className="mt-1 font-heading uppercase tracking-[0.14em] text-[11px] text-black/60 font-semibold">
                      Combat 100% Whey
                    </div>
                  </div>
                </Link>
              </SectionReveal>
            ))}
          </div>

          {/* Preworkout banner */}
          <SectionReveal>
            <div className="mt-16">
              <PlaceholderImage
                label="Product Benefit Banner · Combat Preworkout"
                ratio="banner"
                testId="series-banner-preworkout"
                src={BENEFIT_BANNERS["combat-preworkout"]}
                alt="Combat Preworkout — Four ingredients. Made to perform."
              />
            </div>
          </SectionReveal>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            {combatPreworkout.flavours.map((f, i) => (
              <SectionReveal key={f.id} delay={i * 80}>
                <Link
                  to={`/products/combat-preworkout`}
                  data-testid={`series-preworkout-flavour-${f.id}`}
                  className="block bg-white border border-black/10 hover:border-black transition-colors group"
                >
                  <div className="overflow-hidden">
                    <PlaceholderImage
                      label={`Flavour Banner · ${f.name}`}
                      ratio="square"
                      src={FLAVOUR_BANNERS[f.id]}
                      alt={`${f.name} — Combat Preworkout`}
                      className="transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="p-5 text-center">
                    <div className="font-display text-xl uppercase text-black">{f.name}</div>
                    <div className="mt-1 font-heading uppercase tracking-[0.14em] text-[11px] text-black/60 font-semibold">
                      Combat Preworkout
                    </div>
                  </div>
                </Link>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY MUSCLEPHARM ============ */}
      <section data-testid="why-section" className="bg-[color:var(--mp-off-white)] py-20 lg:py-24 border-y border-black/10">
        <div className="mp-container">
          <SectionReveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <div className="mp-eyebrow-green">Why MusclePharm</div>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
                Serious nutrition for<br />
                serious athletes.
              </h2>
            </div>
          </SectionReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {HIGHLIGHTS.map((h, i) => (
              <SectionReveal key={h.title} delay={i * 100}>
                <div
                  data-testid={`highlight-card-${i}`}
                  className="group h-full bg-white border border-black/10 p-8 hover:border-black transition-colors"
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-black/15 transition-colors group-hover:bg-black group-hover:border-black text-black group-hover:text-white">
                    <h.icon size={20} />
                  </div>
                  <h3 className="mt-6 font-display text-2xl uppercase leading-none">
                    {h.title}
                  </h3>
                  <p className="mt-3 font-body text-black/65 leading-relaxed">
                    {h.body}
                  </p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE COMBAT CREED (clean dark section — no bg image) ============ */}
      <section
        data-testid="testimonials-section"
        className="relative bg-[color:var(--mp-dark)] text-white py-20 lg:py-28 overflow-hidden"
      >
        {/* Clean editorial background — subtle green accent shape, no image */}
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-[0.08] blur-3xl"
          style={{ background: "var(--mp-green)" }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full opacity-[0.05] blur-3xl bg-white"
        />
        {/* Diagonal line pattern */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(135deg, transparent 0px, transparent 22px, rgba(255,255,255,0.6) 22px, rgba(255,255,255,0.6) 23px)",
          }}
        />
        {/* Massive brand watermark */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-8 left-0 right-0 font-display text-[18vw] leading-[0.85] text-white/[0.03] whitespace-nowrap select-none uppercase text-center"
        >
          MUSCLEPHARM
        </div>

        <div className="relative mp-container">
          <SectionReveal>
            <div className="text-center mb-14">
              <div
                className="font-heading uppercase tracking-[0.28em] text-[11px] font-semibold"
                style={{ color: "var(--mp-green)" }}
              >
                The Combat Creed
              </div>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] text-white">
                What we live by.
              </h2>
              <p className="mt-4 max-w-2xl mx-auto font-body text-white/70 leading-relaxed">
                Three principles that shape every product we bring to India.
              </p>
            </div>
          </SectionReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CREED.map((c, i) => (
              <SectionReveal key={c.title} delay={i * 100}>
                <article
                  data-testid={`testimonial-${i}`}
                  className="h-full bg-white/[0.03] border border-white/10 p-8 backdrop-blur-sm hover:border-[color:var(--mp-green)]/60 transition-colors"
                >
                  <div
                    className="font-heading uppercase tracking-[0.28em] text-[11px] font-semibold"
                    style={{ color: "var(--mp-green)" }}
                  >
                    {c.kicker}
                  </div>
                  <h3 className="mt-3 font-display text-2xl sm:text-3xl uppercase leading-[0.95] text-white">
                    {c.title}
                  </h3>
                  <p className="mt-4 font-body text-white/80 leading-relaxed">
                    {c.body}
                  </p>
                </article>
              </SectionReveal>
            ))}
          </div>

          <SectionReveal delay={300}>
            <div className="mt-12 text-center">
              <Link to="/authenticity" className="mp-btn mp-btn-primary">
                Verify Authenticity <ArrowRight size={16} />
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>
    </>
  );
}
